import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const infos = [
  { icon: <Phone size={22} />, label: 'Téléphone', value: '+229 01 56 57 70 66', color: '#FF6B00' },
  { icon: <Mail size={22} />, label: 'Email', value: 'elimdepannage@gmail.com', color: '#10B981' },
  { icon: <MapPin size={22} />, label: 'Adresse', value: 'Cotonou, Bénin', color: '#8B5CF6' },
  { icon: <Clock size={22} />, label: 'Disponibilité', value: '24H/24', color: '#F59E0B' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const formRef = useRef(null);
  const inView = useInView(formRef, { once: true, margin: '-60px' });

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de remplir tous les champs obligatoires.');
      return;
    }
    setSent(true);
    toast.success('Message envoyé ! Nous vous répondrons sous 24h.');
  };

  return (
    <div style={{ paddingTop: '80px', background: 'var(--gray-light)', minHeight: '100vh' }}>

      {/* HERO */}
      <div style={{
        background: 'linear-gradient(135deg, var(--blue-dark) 0%, #0D2137 100%)',
        padding: '5rem 0 4rem',
        textAlign: 'center',
      }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,107,0,0.15)',
              border: '1px solid rgba(255,107,0,0.3)',
              borderRadius: '50px', padding: '0.4rem 1.2rem',
              color: 'var(--orange)', fontSize: '0.8rem',
              fontFamily: 'var(--font-cond)', letterSpacing: '2px',
              textTransform: 'uppercase', marginBottom: '1.5rem',
            }}>
              Contactez-nous
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: 'white', letterSpacing: '2px', marginBottom: '1rem',
            }}>
              ON EST LÀ POUR<br />
              <span style={{ color: 'var(--orange)' }}>VOUS AIDER</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem', maxWidth: 520, margin: '0 auto' }}>
              Une question, un problème ou une suggestion ? Notre équipe répond sous 24h.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container" style={{ padding: '4rem 1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'start' }}>

          {/* INFOS */}
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: '1.6rem', letterSpacing: '1px', marginBottom: '2rem' }}>
              NOS COORDONNÉES
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginBottom: '2.5rem' }}>
              {infos.map((info, i) => (
                <motion.div
                  key={info.label}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    background: 'white', borderRadius: '12px', padding: '1.2rem',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: '12px',
                    background: info.color + '18',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: info.color, flexShrink: 0,
                  }}>
                    {info.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.2rem' }}>
                      {info.label}
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--blue-dark)' }}>{info.value}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* WhatsApp */}
            <a
              href="https://wa.me/2290156577066"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.8rem',
                background: '#25D366', color: 'white',
                borderRadius: '12px', padding: '1rem 1.5rem',
                fontWeight: 700, textDecoration: 'none',
                fontFamily: 'var(--font-cond)', letterSpacing: '1px',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <MessageCircle size={22} />
              Discuter sur WhatsApp
            </a>
          </div>

          {/* FORMULAIRE */}
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ background: 'white', borderRadius: '16px', padding: '2.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
          >
            {sent ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <CheckCircle size={60} color="#10B981" style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: '1.4rem', letterSpacing: '1px', marginBottom: '0.8rem' }}>
                  MESSAGE ENVOYÉ !
                </h3>
                <p style={{ color: 'var(--gray-dark)' }}>Nous vous répondrons dans les 24h. Merci !</p>
                <button
                  onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="btn btn-primary"
                  style={{ marginTop: '1.5rem' }}
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <>
                <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: '1.4rem', letterSpacing: '1px', marginBottom: '1.8rem' }}>
                  ENVOYER UN MESSAGE
                </h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        Nom *
                      </label>
                      <input
                        name="name" value={form.name} onChange={handleChange}
                        placeholder="Votre nom"
                        className="form-input"
                        style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-mid)', borderRadius: '8px', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        Email *
                      </label>
                      <input
                        name="email" type="email" value={form.email} onChange={handleChange}
                        placeholder="votre@email.com"
                        style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-mid)', borderRadius: '8px', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Sujet
                    </label>
                    <input
                      name="subject" value={form.subject} onChange={handleChange}
                      placeholder="Objet de votre message"
                      style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-mid)', borderRadius: '8px', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Message *
                    </label>
                    <textarea
                      name="message" value={form.message} onChange={handleChange}
                      placeholder="Décrivez votre besoin..."
                      rows={5}
                      style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-mid)', borderRadius: '8px', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                    <Send size={18} /> Envoyer le message
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
